import { useQuery } from "@apollo/client";
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  Link,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Typography,
} from "@mui/material";
import { type ReactNode, useEffect, useState } from "react";
import { Title, useTranslate } from "react-admin";
import { useSearchParams } from "react-router-dom";

import { BillwerkPlanVariantValue } from "../components/finance/BillwerkPlanVariantValue";
import { ListPagination } from "../components/ListPagination";
import { RequirePermission } from "../components/RequirePermission";
import { ResourceName } from "../constants/resources";
import {
  BillwerkListStatus,
  BillwerkPlanVariantsDocument,
  type BillwerkPlanVariantsQuery,
  type BillwerkPlanVariantsQueryVariables,
  BillwerkPlanVariantState,
  BillwerkPlanVariantView,
} from "../generated/graphql";
import { formatCrmCount, formatCrmDateTime } from "../lib/crm";
import { typography } from "../theme/designTokens";

type PlanVariants = BillwerkPlanVariantsQuery["billwerkPlanVariants"];
type Entry = PlanVariants["entries"][number];
type Item = PlanVariants["items"][number];
type Count = PlanVariants["entryCounts"][number];

// The server's page size.
const PAGE_SIZE = 50;
const POLL_MS = 5000;
const SEARCH_DEBOUNCE_MS = 300;

const VIEWS = [BillwerkPlanVariantView.Entries, BillwerkPlanVariantView.Items];
// The filter chips follow the order of the states.
const STATES = Object.values(BillwerkPlanVariantState);

const isView = (value: string | null): value is BillwerkPlanVariantView =>
  VIEWS.includes(value as BillwerkPlanVariantView);

const isState = (value: string | null): value is BillwerkPlanVariantState =>
  STATES.includes(value as BillwerkPlanVariantState);

const total = (counts: Count[]): number => counts.reduce((sum, count) => sum + count.count, 0);

const SalesforceLink = ({ href, children }: { href: string; children: ReactNode }) => (
  <Link href={href} target="_blank" rel="noopener noreferrer">
    {children}
  </Link>
);

const Secondary = ({ children }: { children: ReactNode }) => (
  <Typography variant="body2" color="text.secondary">
    {children}
  </Typography>
);

const EntryRow = ({ entry }: { entry: Entry }) => {
  const translate = useTranslate();
  return (
    <TableRow data-testid="billwerk-plan-variant-entry">
      <TableCell sx={{ verticalAlign: "top" }}>
        <Typography>{entry.priceBookName ?? entry.priceBookId}</Typography>
        <Stack direction="row" gap={0.5} mt={0.5}>
          {entry.priceBookStandard && (
            <Chip size="small" variant="outlined" label={translate("billwerkPlanVariants.standard")} />
          )}
          {entry.priceBookActive === false && (
            <Chip size="small" variant="outlined" label={translate("billwerkPlanVariants.priceBookInactive")} />
          )}
        </Stack>
      </TableCell>
      <TableCell sx={{ verticalAlign: "top" }}>
        <SalesforceLink href={entry.salesforceUrl}>{entry.productName ?? entry.productId}</SalesforceLink>
        {entry.productFamily && <Secondary>{entry.productFamily}</Secondary>}
        {entry.active === false && (
          <Chip size="small" variant="outlined" sx={{ mt: 0.5 }} label={translate("billwerkPlanVariants.inactive")} />
        )}
      </TableCell>
      <TableCell sx={{ verticalAlign: "top" }}>{entry.currency ?? "–"}</TableCell>
      <TableCell sx={{ verticalAlign: "top" }}>{entry.planVariant.billwerkPlanId ?? "–"}</TableCell>
      <TableCell sx={{ verticalAlign: "top" }}>
        <BillwerkPlanVariantValue planVariant={entry.planVariant} />
      </TableCell>
      <TableCell sx={{ verticalAlign: "top" }} align="right">
        {formatCrmCount(entry.billingItems)}
      </TableCell>
    </TableRow>
  );
};

const ItemRow = ({ item }: { item: Item }) => {
  const translate = useTranslate();
  return (
    <TableRow data-testid="billwerk-plan-variant-item">
      <TableCell sx={{ verticalAlign: "top" }}>
        <SalesforceLink href={item.salesforceUrl}>{item.name ?? item.salesforceId}</SalesforceLink>
        {item.status && <Secondary>{item.status}</Secondary>}
        <Stack direction="row" gap={0.5} mt={0.5}>
          {item.billed && <Chip size="small" variant="outlined" label={translate("billwerkPlanVariants.billed")} />}
          {item.openNotification && (
            <Chip size="small" variant="outlined" label={translate("billwerkPlanVariants.openNotification")} />
          )}
        </Stack>
      </TableCell>
      <TableCell sx={{ verticalAlign: "top" }}>{item.accountName ?? "–"}</TableCell>
      <TableCell sx={{ verticalAlign: "top" }}>
        {item.contractUrl ? (
          <SalesforceLink href={item.contractUrl}>
            {item.contractNumber ?? translate("billwerkPlanVariants.contract")}
          </SalesforceLink>
        ) : (
          "–"
        )}
      </TableCell>
      <TableCell sx={{ verticalAlign: "top" }}>{item.productName ?? "–"}</TableCell>
      <TableCell sx={{ verticalAlign: "top" }}>{item.planVariant.priceBookName ?? "–"}</TableCell>
      <TableCell sx={{ verticalAlign: "top" }}>
        <BillwerkPlanVariantValue planVariant={item.planVariant} />
      </TableCell>
    </TableRow>
  );
};

const BillwerkPlanVariantsView = () => {
  const translate = useTranslate();
  const [params, setParams] = useSearchParams();
  const viewParam = params.get("view");
  const view = isView(viewParam) ? viewParam : BillwerkPlanVariantView.Entries;
  const stateParam = params.get("state");
  const state = isState(stateParam) ? stateParam : null;
  const search = params.get("q") ?? "";
  const page = Math.max(0, Number(params.get("page")) || 0);
  const [searchInput, setSearchInput] = useState(search);

  const update = (changes: Record<string, string | null>) =>
    setParams(
      current => {
        const next = new URLSearchParams(current);
        Object.entries(changes).forEach(([key, value]) => (value === null ? next.delete(key) : next.set(key, value)));
        return next;
      },
      { replace: true }
    );

  useEffect(() => {
    const timer = window.setTimeout(() => {
      if (searchInput.trim() !== search) update({ q: searchInput.trim() || null, page: null });
    }, SEARCH_DEBOUNCE_MS);
    return () => window.clearTimeout(timer);
    // Only the typed text starts a new search.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchInput]);

  const variables: BillwerkPlanVariantsQueryVariables = {
    view,
    state,
    search: search || null,
    offset: page * PAGE_SIZE,
  };
  const { data, loading, error, startPolling, stopPolling } = useQuery<
    BillwerkPlanVariantsQuery,
    BillwerkPlanVariantsQueryVariables
  >(BillwerkPlanVariantsDocument, { variables, fetchPolicy: "cache-and-network" });
  const result = data?.billwerkPlanVariants;
  const status = result?.billwerk.status;
  const pending = status === BillwerkListStatus.Loading;
  useEffect(() => {
    if (!result || loading || pending || error) return;
    const lastPage = Math.max(0, Math.ceil(result.totalCount / PAGE_SIZE) - 1);
    if (page > lastPage) update({ page: lastPage > 0 ? String(lastPage) : null });
    // Clamp only after the current page has loaded successfully.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [result, loading, pending, error, page]);

  // A slow Billwerk read goes on in the background: ask again until it is there.
  useEffect(() => {
    if (pending) startPolling(POLL_MS);
    else stopPolling();
    return () => stopPolling();
  }, [pending, startPolling, stopPolling]);
  const counts = result ? (view === BillwerkPlanVariantView.Entries ? result.entryCounts : result.itemCounts) : [];
  const countOf = (option: BillwerkPlanVariantState): number =>
    counts.find(count => count.state === option)?.count ?? 0;
  // The chosen state stays, even without rows, so "Alle" can clear it.
  const states = STATES.filter(option => countOf(option) > 0 || option === state);
  const rows = result ? (view === BillwerkPlanVariantView.Entries ? result.entries.length : result.items.length) : 0;
  const title = translate("billwerkPlanVariants.title");
  const unreadable = result?.salesforceSync.unreadableFields.join(", ") ?? "";

  return (
    <Stack gap={2} data-testid="billwerk-plan-variants">
      <Title title={title} />
      <Box>
        <Typography variant="overline" color="text.secondary">
          {translate("financeNotifications.section")}
        </Typography>
        <Typography component="h1" variant="h5" fontWeight={typography.fontWeight.semibold}>
          {title}
        </Typography>
        <Typography color="text.secondary">{translate("billwerkPlanVariants.intro")}</Typography>
      </Box>
      {result && (
        <Box>
          {result.billwerk.loadedAt && (
            <Typography color="text.secondary">
              {translate("billwerkPlanVariants.loadedAt", {
                time: formatCrmDateTime(result.billwerk.loadedAt),
                count: formatCrmCount(result.billwerk.variantCount ?? 0),
              })}
            </Typography>
          )}
          <Typography color="text.secondary">
            {result.salesforceSync.lastSucceededAt
              ? translate("billwerkPlanVariants.lastSync", {
                  time: formatCrmDateTime(result.salesforceSync.lastSucceededAt),
                })
              : translate("billwerkPlanVariants.noSync")}
          </Typography>
        </Box>
      )}
      {error && <Alert severity="error">{translate("billwerkPlanVariants.loadError")}</Alert>}
      {loading && !data && <CircularProgress aria-label={translate("billwerkPlanVariants.loading")} />}
      {result && !result.withheld && pending && (
        <Alert severity="info" icon={<CircularProgress size={20} />}>
          {translate("billwerkPlanVariants.status.LOADING")}
        </Alert>
      )}
      {result && !result.withheld && status === BillwerkListStatus.Unavailable && (
        <Alert severity="error">{translate("billwerkPlanVariants.status.UNAVAILABLE")}</Alert>
      )}
      {result && !result.withheld && status === BillwerkListStatus.NotConfigured && (
        <Alert severity="warning">{translate("billwerkPlanVariants.status.NOT_CONFIGURED")}</Alert>
      )}
      {result?.withheld && (
        <Alert severity="warning">
          {unreadable
            ? translate("billwerkPlanVariants.withheld", { fields: unreadable })
            : translate("billwerkPlanVariants.notCopied")}
        </Alert>
      )}
      {result && !result.withheld && unreadable && (
        <Alert severity="info">{translate("billwerkPlanVariants.unreadable", { fields: unreadable })}</Alert>
      )}
      {result && !result.withheld && (
        <>
          <Tabs
            value={view}
            onChange={(_event, next: BillwerkPlanVariantView) => update({ view: next, state: null, page: null })}
            aria-label={translate("billwerkPlanVariants.viewFilter")}
            variant="scrollable"
          >
            {VIEWS.map(option => (
              <Tab
                key={option}
                value={option}
                label={translate("billwerkPlanVariants.viewCount", {
                  view: translate(`billwerkPlanVariants.views.${option}`),
                  count: formatCrmCount(
                    total(option === BillwerkPlanVariantView.Entries ? result.entryCounts : result.itemCounts)
                  ),
                })}
              />
            ))}
          </Tabs>
          <Typography color="text.secondary">{translate(`billwerkPlanVariants.viewHints.${view}`)}</Typography>
          {(states.length > 1 || state !== null) && (
            <Stack
              direction="row"
              flexWrap="wrap"
              gap={1}
              role="group"
              aria-label={translate("billwerkPlanVariants.stateFilter")}
            >
              {[null, ...states].map(option => (
                <Chip
                  key={option ?? "all"}
                  label={
                    option
                      ? `${translate(`billwerkPlanVariants.states.${option}`)} (${formatCrmCount(countOf(option))})`
                      : translate("billwerkPlanVariants.allStates")
                  }
                  color={option === state ? "primary" : "default"}
                  variant={option === state ? "filled" : "outlined"}
                  aria-pressed={option === state}
                  onClick={() => update({ state: option, page: null })}
                />
              ))}
            </Stack>
          )}
          <TextField
            label={translate("billwerkPlanVariants.search")}
            helperText={translate("billwerkPlanVariants.searchHint")}
            value={searchInput}
            onChange={event => setSearchInput(event.target.value)}
            size="small"
            sx={{ maxWidth: 420 }}
          />
          <TableContainer>
            {view === BillwerkPlanVariantView.Entries ? (
              <Table size="small" aria-label={translate("billwerkPlanVariants.views.ENTRIES")}>
                <TableHead>
                  <TableRow>
                    <TableCell>{translate("billwerkPlanVariants.columns.priceBook")}</TableCell>
                    <TableCell>{translate("billwerkPlanVariants.columns.product")}</TableCell>
                    <TableCell>{translate("billwerkPlanVariants.columns.currency")}</TableCell>
                    <TableCell>{translate("billwerkPlanVariants.columns.plan")}</TableCell>
                    <TableCell>{translate("billwerkPlanVariants.columns.planVariant")}</TableCell>
                    <TableCell align="right">{translate("billwerkPlanVariants.columns.billingItems")}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {result.entries.map(entry => (
                    <EntryRow key={entry.salesforceId} entry={entry} />
                  ))}
                </TableBody>
              </Table>
            ) : (
              <Table size="small" aria-label={translate("billwerkPlanVariants.views.ITEMS")}>
                <TableHead>
                  <TableRow>
                    <TableCell>{translate("billwerkPlanVariants.columns.item")}</TableCell>
                    <TableCell>{translate("billwerkPlanVariants.columns.account")}</TableCell>
                    <TableCell>{translate("billwerkPlanVariants.columns.contract")}</TableCell>
                    <TableCell>{translate("billwerkPlanVariants.columns.product")}</TableCell>
                    <TableCell>{translate("billwerkPlanVariants.columns.priceBook")}</TableCell>
                    <TableCell>{translate("billwerkPlanVariants.columns.planVariant")}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {result.items.map(item => (
                    <ItemRow key={item.salesforceId} item={item} />
                  ))}
                </TableBody>
              </Table>
            )}
          </TableContainer>
          {rows === 0 && (
            <Typography color="text.secondary">{translate(`billwerkPlanVariants.empty.${view}`)}</Typography>
          )}
          {result.totalCount > PAGE_SIZE && (
            <ListPagination
              total={result.totalCount}
              page={page + 1}
              perPage={PAGE_SIZE}
              resource={ResourceName.BillwerkPlanVariants}
              onPageChange={next => update({ page: next > 1 ? String(next - 1) : null })}
              isLoading={loading}
            />
          )}
        </>
      )}
    </Stack>
  );
};

export const BillwerkPlanVariantsPage = () => (
  <RequirePermission permission={ResourceName.BillwerkPlanVariants}>
    <BillwerkPlanVariantsView />
  </RequirePermission>
);
